import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  RETENTION_ENDED_COPY,
  confirmSelection,
  failedImagesAmountNotice,
  formatDateTime,
  isResultReceiveClosed,
  isResultReceiveExpired,
  RESULT_RECEIVE_EXPIRED_COPY,
  refundableTotal,
  retentionEndedNotice,
  type JobImageView,
} from "@makit/shared";

import { PageHead } from "../../app/PageHead";
import { Button, CheckerThumb } from "../../components/primitives";
import { LimitBanner, PayNow } from "../../components/banners";
import { Toast } from "../../components/feedback";
import { ApiError, approveJob, absoluteUrl, fetchJobBilling, fetchJobEvents } from "../../lib/api";
import { thumbUrlOf } from "../../lib/rendition";
import { reviveBranchOf, type ShellJob } from "../../lib/shell-job";
import { useJobView } from "../../lib/use-job-view";
import { settlementPath } from "../../lib/settlement";
import { rejectionToast } from "../../lib/edit-messages";
import { uploadStagePathFor } from "./Uploading";

/** `W-31` 결과 확인. 앱 `A-31`·`A-32` 가 여기 모인다.
 *
 * **`A-32`(나갔다가 돌아왔을 때)를 별도 화면으로 두지 않고 이 화면의 맨 위 줄로 넣었다.**
 * 웹은 대개 나갔다 돌아온 뒤에 여는 화면이라 그게 기본 상태다.
 *
 * **폰은 넘기면서 보고 웹은 한 번에 본다.** `A-31` 이 카드 하나씩 넘기는 것은 좁아서 그런
 * 것이지 그렇게 보는 편이 나아서가 아니다. 고르는 기준이 장끼리의 비교라서다.
 *
 * ## ⭐ 2026-09-24. 격자가 **보여 주기만 하던 것에서 정하는 것**이 됐다 (`13.1` O-73)
 *
 * **보여 주는 것과 바꿀 수 있는 것은 다르고, 고름이 성립하려면 안 고르는 쪽이 있어야 한다**
 * (법무 성질 ㄱ. PRD `7.7.3a`). 폰은 캐러셀이 한 번에 한 장이라 화면을 따로 세웠지만
 * (`A-34`) **여기는 여덟 칸이 이미 한눈에 보여 화면이 늘지 않았다.**
 *
 * ⛔ **판정을 이 파일에서 다시 쓰지 않는다.** 켠 장·장수·금액이 전부 `@makit/shared` 의
 * `confirmSelection` 에서 오고 **앱 `A-34` 도 같은 함수를 부른다.** 각자 세면 같은 작업이
 * 폰에서 7장, PC 에서 8장으로 청구된다.
 *
 * ⛔ **`approveJob(job.id)` 로 되돌리지 마라.** 인자가 선택적이라 **타입체크로도 런타임으로도
 * 안 잡히는데**, 빼는 순간 서버가 **만든 장을 전부 얼리고 전부 청구한다.** */
export function ResultStage() {
  const job = useOutletContext<ShellJob>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { view } = useJobView(job.id);

  const [toast, setToast] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  /** ⭐ **서버가 받기 기간이 지났다고 409 로 답한 뒤**(PRD `7.6.14c` ①②). 화면이 기한을 셈하지
   *  않고, 결과 주소가 `null` 인 것만으로 켜지 않는다(서버는 판정을 못 했을 때도 주소를 비운다). */
  const [receiveRejected, setReceiveRejected] = useState(false);
  /** ⭐ **기한 지남 상태.** 둘 중 하나로 켜진다(PRD `7.6.14c` ①). 서버가 작업 보기에서
   *  `resultReceive.state === "CLOSED"` 로 판정했거나(#218. 처음 열 때부터), 받기 경로가 409
   *  `RESULT_RECEIVE_EXPIRED` 로 답했거나(옛 서버 · 그사이 지난 경우). ⛔ `UNKNOWN` 은 켜지 않는다.
   *  ⛔ 결과 주소 `null` 로 대신하지 않는다(서버는 판정을 못 했을 때도 주소를 비운다). */
  const receiveClosed = receiveRejected || isResultReceiveClosed(view);
  /** ⭐ **끈 장을 들고 있다. 켠 장이 아니다.** 기본이 전부 켜짐이라 화면이 기억할 것은
   *  셀러가 손댄 것뿐이고, 그래야 다시 만들어 성공한 장이 **켜진 채로** 합류한다. */
  const [offIds, setOffIds] = useState<ReadonlySet<string>>(() => new Set<string>());
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  function announce(message: string) {
    setToast(message);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToast(null), 4000);
  }

  // ── 끝난 시각 ────────────────────────────────────────────────────────────
  //
  // ⛔ **`updatedAt` 도 `lastActivity.at` 도 쓰지 않는다.** 승인하거나 상품명을 고치면 그 두
  // 칸이 밀려 올라가 **"끝났습니다"가 조용히 편집 시각을 가리킨다.** 끝난 시각의 정본은
  // 이력의 `CUTOUT_FINISHED` 줄 하나다. 그 줄이 없으면 **문장을 아예 그리지 않는다** —
  // 줄표로 채우면 없는 사실을 있는 것처럼 말하게 된다.
  const events = useQuery({
    queryKey: ["job-events", job.id],
    queryFn: () => fetchJobEvents(job.id),
    enabled: Boolean(view),
  });
  const finishedAt = events.data?.items.find((e) => e.type === "CUTOUT_FINISHED")?.occurredAt;
  const finishedLabel = finishedAt ? formatDateTime(finishedAt) : "";

  // ── 정산 ─────────────────────────────────────────────────────────────────
  //
  // **화면이 곱하지 않는다**(PRD 7.7.8). 금액은 서버가 셈한 값이고, 청구 행이 아직 없으면
  // `paymentId` 가 `null` 이라 **배너를 그리지 않는다** — 예시 금액을 남기면 돈을 거짓으로
  // 말하게 된다.
  const billing = useQuery({
    queryKey: ["job-billing", job.id],
    queryFn: () => fetchJobBilling(job.id),
    enabled: Boolean(view),
  });

  // ── ⭐ 이번 판에 들어갈 장 (PRD `7.7.3a` `W-31` · `7.6.13f`) ───────────────
  //
  const selection = useMemo(
    () =>
      view
        ? confirmSelection(view, offIds)
        : {
            on: [] as JobImageView[],
            confirmedImageIds: [] as string[],
            onCount: 0,
            amount: 0,
            canConfirm: false,
            heroOff: false,
          },
    [view, offIds],
  );

  function toggle(imageId: string) {
    setOffIds((prev) => {
      const next = new Set(prev);
      if (next.has(imageId)) next.delete(imageId);
      else next.add(imageId);
      return next;
    });
  }

  const images = view ? [...view.images].sort((a, b) => a.sortOrder - b.sortOrder) : [];
  const made = images.filter((img) => img.status === "DONE").length;
  const failed = images.filter((img) => img.status === "FAILED").length;
  /** `이 장 다시 만들기` 가 주소에 싣는 장. ⚠️ **어느 장이 「이 장」인지(PRD `7.13` D-95 ⓐ)는 미결이다.**
   *  지금은 올린 순서로 첫 번째, 다시 만들 수 있는 장(`reviveBranchOf` 가 `null` 이 아닌 장)이고, 이것은
   *  `W-21` 이 `?image=` 없이 고르던 장과 같다. 그래서 **동작은 전과 같고 장 id 를 싣는 길만 놓였다.** */
  const remakeTarget = view ? images.find((img) => reviveBranchOf(img, view) !== null) : undefined;
  // 앱 `A-31` 과 같은 판정이다(`result.tsx` 의 `refundAmountKnown`). 하나라도 알면 그 합을 말한다.
  const refund = view ? refundableTotal(view) : { amount: 0, imageCount: 0, unknownCount: 0 };
  const refundKnown = refund.unknownCount < refund.imageCount;
  /** ⭐ **앱 `A-31f` 와 같은 사실**(PRD `7.6.13g`). 못 만든 장 중 올린 날부터 30일이 지나 원본이
   *  없는 장. 판정과 배너 글자는 `retentionEndedNotice` 하나가 정한다(앱과 같은 `recallBlockOf`).
   *  ⭐ **보관은 장마다다.** 한 작업에 끝난 장과 아직인 장이 섞일 수 있어 l1 이 장을 가리키고
   *  (`n번 사진은` · `못 만든 k장은` · `못 만든 f장 중 k장은`), 칸에 `원본 없음` 이 선다
   *  (2026-10-06 design-manager 확정). 없으면 `null` 이고 배너도 칸 표시도 서지 않는다. */
  const retentionNotice = view ? retentionEndedNotice(view) : null;

  async function approve() {
    setApproving(true);
    try {
      // ⭐ **여기가 셀러의 확정이 서버로 나가는 자리다.** 두 번째 인자를 빼면 서버가
      //    **만든 장을 전부 얼리고 전부 청구한다**(`PublishInput.confirmedImageIds`).
      const res = await approveJob(job.id, selection.confirmedImageIds);
      // `created === false` 는 실패가 아니다 — 마지막 판과 상태가 같아 그 판을 그대로
      // 가리킨 것이다(두 번 누르기 · 응답 유실 재시도). 가르는 것은 문구뿐이다.
      announce(
        res.created
          ? `v${res.version.versionNo}을 만들었어요`
          : `이미 같은 내용의 v${res.version.versionNo}이 있어요`,
      );
      // 판 번호가 머리줄(`현재 v1`)과 내보내기 목록에 함께 걸려 있다. 셋을 한 번에 새로
      // 읽지 않으면 방금 만든 판을 화면이 모른 채 남는다.
      await queryClient.invalidateQueries({ queryKey: ["job", job.id] });
      await queryClient.invalidateQueries({ queryKey: ["job-versions", job.id] });
      await queryClient.invalidateQueries({ queryKey: ["job-events", job.id] });
    } catch (err) {
      // ⛔ 실패를 삼키지 않는다. 삼키면 셀러는 확정됐다고 믿고 이 화면을 떠난다.
      // **사유마다 할 말이 다르다** — 만든 장이 없어서 거절된 것을 「잠시 뒤」라고 하면
      // 셀러는 풀리지 않을 것을 기다린다(`lib/edit-messages.ts`).
      // ⭐ 받기 기간 409 는 「다시」를 붙이지 않는 shared 문장이다(PRD `7.6.14c` ②).
      if (err instanceof ApiError && isResultReceiveExpired(err)) {
        setReceiveRejected(true);
        announce(RESULT_RECEIVE_EXPIRED_COPY.rejected);
      } else {
        announce(rejectionToast(err, "확정하"));
      }
    } finally {
      setApproving(false);
    }
  }

  return (
    <>
      <PageHead
        frame="W-31"
        title="결과 확인"
        right={`${made}장 완료 · ${failed}장 실패`}
      />

      {/* ⭐ 받기 기간이 지났다고 서버가 답했으면 맨 위에 선다(PRD `7.6.14c` ①). */}
      {receiveClosed ? (
        <LimitBanner>
          <span className="l1">{RESULT_RECEIVE_EXPIRED_COPY.head}</span>
          <span className="l2">{RESULT_RECEIVE_EXPIRED_COPY.reason}</span>
        </LimitBanner>
      ) : null}

      <div className="push" style={{ margin: "16px 0 12px" }}>
        <span className="ico" aria-hidden="true">
          ✓
        </span>
        <span>
          <span className="h">나가 계신 동안 {made}장을 만들었어요</span>
          <span className="b">
            {/* 끝난 시각을 못 읽으면 그 조각만 빼고 문장을 세운다(`formatDateTime` 의 빈
                문자열 계약). 기호나 배지로 자리를 채우지 않는다. */}
            {finishedLabel ? `${finishedLabel}에 끝났습니다 · ` : ""}
            {failed > 0 ? `${failed}장은 못 만들었습니다` : "못 만든 장은 없습니다"}
          </span>
        </span>
      </div>

      {/* 정산은 여기서 말하고 **명세는 계정 쪽에서 본다**. 금액이 이미 돌아갔다는 사실은
          결과와 함께 알아야 하지만, 줄 단위 명세는 결제 이력 쪽이 제자리다.
          그래서 이 배너 자체가 그리로 가는 문이다 — 꺾쇠 `›` 는 "다른 화면으로 간다"는 뜻이고
          (`⌄` 가 아니다), 갈 곳이 없는 줄에 꺾쇠를 붙이지 않는 것의 반대도 지킨다:
          갈 곳이 있는데 누를 수 없게 두지 않는다(PRD 7.5.4 · 7.7.4). */}
      {failed > 0 && billing.data?.paymentId ? (
        <button
          type="button"
          className="limit link"
          onClick={() => navigate(settlementPath(job.id))}
          aria-label="정산 명세 보기"
        >
          <span className="ico" aria-hidden="true">
            ₩
          </span>
          <span className="body">
            {/* ⭐ **목업 `W-31` 글자 그대로**(2026-10-02. 앱 `A-31` 과 같은 함수). 돈은 셀러가
                고를 때까지 기다린다. 다시 만들면 그 장에 쓰이고 안 만들면 돌아간다(2026-09-23
                확정). **조건절이 이 배너의 전부다.**
                ⛔ 화면이 곱하지 않는다. 금액은 장마다 서버가 저장한 스냅샷(`refundableAmount`)을
                더한 값이고, 하나도 모르면 금액을 빼고 말한다(`₩0` 을 지어내지 않는다). */}
            <span className="l1">
              {failedImagesAmountNotice(failed, refundKnown ? refund.amount : null)} 신청할 것이
              없습니다.
            </span>
            <span className="l2">
              {/* `실행 방식 미정` 배지를 걷었다 (2026-10-04). 돈이 돌아가는 시점은 공개 환불정책
                  제4조와 정산 명세의 `REFUND_ARRIVAL_NOTICE` 가 말한다. */}
              다시 만들면 그 금액으로 만들고 더 내실 것이 없습니다. <b>정산 명세 보기</b>
            </span>
          </span>
          <span className="chev" aria-hidden="true">
            ›
          </span>
        </button>
      ) : null}

      {/* ⭐ **`A-31f` 와 같은 두 줄.** 목업의 뒤 문장(못 만든 날부터 30일에 돌아갑니다)은 앱과
          같은 근거로 뺐다(`LEGAL_IP_GUIDE.md` `6A.12`). 돈 문장은 위 `₩` 줄이 그대로 맡는다. */}
      {retentionNotice ? (
        <LimitBanner icon="!">
          <span className="l1">{retentionNotice.l1}</span>
          <span className="l2">{retentionNotice.l2}</span>
        </LimitBanner>
      ) : null}

      {/* ⭐ **격자 위 한 줄.** ⛔ 이 줄이 없으면 **격자를 누를 수 있다는 사실이 화면 어디에도
          없다** — 칸이 그대로 보이기만 하던 회차와 그림이 같기 때문이다.
          ⛔ **딱지를 붙이지 않는다. 기본은 전부 켜짐이다**(PRD `7.6.13f` 판정 1).
          셀러가 손댄 뒤에는 **지금 사실**을 말한다 — 두 장을 끄고도 `전부 켜 두었습니다` 라고
          적으면 화면이 자기 격자를 부정한다. */}
      {made > 0 ? (
        <p className="hint" style={{ margin: "16px 0 8px" }}>
          {selection.onCount === made
            ? `${made}장이 전부 판에 들어가도록 켜 두었습니다. 누르면 끄고 켭니다. `
            : `${made}장 중 ${selection.onCount}장이 판에 들어갑니다. 누르면 끄고 켭니다. `}
          끄신 장은 판에 안 들어가고 그 장 금액은 돌아갑니다. 사진은 그대로 있어 다음 판에 다시
          넣으실 수 있습니다.
        </p>
      ) : null}

      <div className="pickgrid" style={{ margin: made > 0 ? "0 0 14px" : "16px 0 14px" }}>
        {images.map((img, i) => (
          <ResultCell
            key={img.id}
            image={img}
            index={i}
            images={images}
            on={!offIds.has(img.id)}
            retentionEnded={retentionNotice?.imageIds.has(img.id) ?? false}
            onToggle={() => toggle(img.id)}
          />
        ))}
      </div>

      {/* ⭐ **대표컷 자리가 비는 것을 화면이 말은 한다**(`13.1` O-73 후속. 정책 미정).
          블록 배정은 **올린 순번**이 정해서(`blockTypeForUploadOrder`) 1번 사진을 끄고
          확정하면 그 판에 대표컷 블록이 없다. ⛔ **막지 않는다** — 막을지 순번을 다시 매길지는
          제품 결정이고, 결정 전에 코드가 먼저 고르면 그 선택이 정책이 된다. 다만 **아무 말도
          안 하는 상태로 두지 않는다.** 앱 `A-34` 도 같은 자리에 같은 배너를 세운다. */}
      {selection.heroOff ? (
        <LimitBanner icon="!">
          <span className="l1">
            <b>1번 사진을 끄셨습니다.</b> 이 판에는 맨 위에 들어갈 <b>대표컷</b>이 없습니다.
          </span>
        </LimitBanner>
      ) : null}

      {/* 웹은 하단 폭 제약이 없어 셋을 한 줄에 둘 수 있다. "하단에 셋을 두지 않는다"는
          폰 액션바의 규칙이고 웹 본문 동작 줄에는 걸리지 않는다(PRD 7.5.1). */}
      <div className="actions">
        {/* ⛔ **`지금 결제할 금액` 이 아니다.** 선불로 이미 냈으므로 여기서 새로 나가는 돈이
            없고, 그 낱말을 쓰면 **또 결제되는 것**으로 읽힌다(PRD `7.5.9` 판정 2·9).
            금액이 이 화면에서 갈리므로 금액 줄이 필수다(`7.5.2`). */}
        <PayNow label="더 내실 금액" amount="없음" />
        <Button
          label={approving ? "확정하는 중…" : "좋아요 · 이 판으로 확정"}
          small
          disabled={approving || receiveClosed || !selection.canConfirm}
          {...(receiveClosed
            ? { disabledReason: RESULT_RECEIVE_EXPIRED_COPY.disabledReason }
            : made === 0
              ? { disabledReason: "만든 장이 하나도 없어 확정할 것이 없습니다." }
              : !selection.canConfirm
                ? { disabledReason: "판에 들어갈 장이 하나도 켜져 있지 않습니다." }
                : {})}
          onClick={() => void approve()}
        />
        <Button
          label="고치기"
          variant="ghost"
          small
          onClick={() => navigate(`/jobs/${job.id}/review`)}
        />
        <Button
          label="이 장 다시 만들기"
          variant="ghost"
          small
          // 받기 기간이 지나면 다시 만들어도 받을 수 없다(PRD `7.6.14c` ①).
          disabled={receiveClosed}
          {...(receiveClosed ? { disabledReason: RESULT_RECEIVE_EXPIRED_COPY.disabledReason } : {})}
          // ⚠️ **주소에 장 id 를 싣는 길만 놓았다**(PRD `7.13` D-95 ⓑ). **어느 장이 「이 장」인지(D-95 ⓐ)는
          //    미결이다.** 지금은 첫 번째로 다시 만들 수 있는 장이라 `W-21` 의 옛 기본값과 같고 동작이 바뀌지
          //    않았다. 웹 `W-21` 이 다시 만드는 장은 못 만든 장뿐이다(만든 장 다시 만들기는 M-14 미반영으로 꺼 둠).
          //    그런 장이 없으면 작업 주소만 싣는다. ⛔ 라벨은 바꾸지 않는다(`7.5.1a`).
          onClick={() =>
            remakeTarget
              ? navigate(uploadStagePathFor(job.id, remakeTarget.id))
              : navigate(`/jobs/${job.id}/upload`)
          }
        />
        <span className="why">
          {/* ⛔ **다음 판 번호를 화면이 세지 않는다.** 세면 그 사이에 다른 기기가 만든 판과
              어긋난다. 번호는 누른 **뒤** 응답이 들고 온다.
              ⭐ **`켜 둔 장으로` 가 앞에 붙는다**(PRD `7.7.3a`). ⛔ 그 말이 없으면 격자를 끈
                 것이 판에 반영되는지를 화면이 말하지 않는다. */}
          확정하면 <b>켜 둔 장으로</b> 판이 만들어지고 <b>이력에 줄이 쌓입니다.</b> 지금 판에
          담기는 것은 <b>배경을 지운 장</b>이고 <b>스펙표 · 유의사항은 아직 들어가지 않습니다.</b>{" "}
          고치기는 <b>검토 · 고치기</b>에서 이어집니다.
        </span>
      </div>

      {/* ⛔ 「폰은 넘기면서 보고 웹은 한 번에 봅니다」 콜아웃, 「확정한 판에 담기는 것은…」 배너,
          「이 화면에서 아직 못 합니다」를 걷었다 (2026-10-04 사용자 지시). 설계자에게 하는 말이었고,
          판에 담기는 것이 무엇인지는 확정 버튼 옆 `why` 줄이 누르기 전에 말한다. */}

      {toast ? <Toast message={toast} /> : null}
    </>
  );
}

/** 격자 한 칸. **만든 장은 눌러서 끄고 켠다**(PRD `7.7.3a` `W-31`).
 *
 * ⚠️ **번호는 만든 장만 따로 센다.** 전체 인덱스(`i + 1`)를 쓰면 못 만든 장이 중간에 있을 때
 * **만든 장의 번호가 건너뛴다**(`1 · 2 · ✕ · 4`). 목업은 실패가 맨 끝이라 그 자리가 안
 * 드러났고, 실제 작업에서는 중간에 실패한 장이 흔하다.
 *
 * ⛔ **못 만든 장은 `button` 이 아니다.** 켤 수 없는 것을 켤 수 있어 보이게 두지 않는다
 * (`7.1` 원칙 4). 그 칸에 `role="button"` 을 주면 스크린리더 셀러가 눌러 보고 나서 안다. */
function ResultCell({
  image,
  index,
  images,
  on,
  retentionEnded,
  onToggle,
}: {
  image: JobImageView;
  index: number;
  images: readonly JobImageView[];
  on: boolean;
  /** 앱 `A-31f` 의 장인가. 스크린리더 줄에 `원본은 이제 없습니다.` 를 붙인다. */
  retentionEnded: boolean;
  onToggle: () => void;
}) {
  const made = image.status === "DONE";
  const madeNumber = images.slice(0, index + 1).filter((i) => i.status === "DONE").length;
  const src = absoluteUrl(thumbUrlOf(image));
  // ⛔ **이름은 캐러셀 위치가 아니라 올린 순번이다**(PRD `7.6.13b`). `A-34` 가 같은 사진을
  //    같은 이름으로 부른다 — 갈리면 폰에서 끈 장과 PC 에서 끈 장이 달라진다.
  const name = `${image.sortOrder + 1}번 사진`;

  const inner = (
    <>
      {/* 결과물이 있으면 그것을 그린다. 없는 장(못 만든 장·아직 안 만든 장)은 예시 실루엣
          없이 빈 칸으로 둔다 — 실루엣을 그리면 만들어진 것처럼 보인다.
          **`CheckerThumb` 을 거치는 이유는 격자 CSS 가 `.thumb` 을 겨냥하기 때문이다.** */}
      <CheckerThumb fill {...(src ? { src } : {})} />
      <span className="mark mono" aria-hidden="true">
        {made ? madeNumber : "✕"}
      </span>
    </>
  );

  if (!made) {
    return (
      <span className="cell bad">
        {inner}
        {/* ⭐ **보관이 끝난 칸만 눈에 보이는 표시가 선다**(`A-31f`. 2026-10-06 design-manager).
            배너가 둘 이상을 `못 만든 k장` 으로 부를 때 어느 칸인지 가리키는 자리다.
            스크린리더는 아래 sr-only 줄이 같은 사실을 읽으므로 여기는 숨긴다. */}
        {retentionEnded ? (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              left: 4,
              bottom: 4,
              fontSize: 10,
              fontWeight: 700,
              lineHeight: 1.2,
              padding: "1px 4px",
              borderRadius: 4,
              background: "var(--danger-soft)",
              color: "var(--danger)",
            }}
          >
            {RETENTION_ENDED_COPY.cellMark}
          </span>
        ) : null}
        {/* ⛔ **파일명을 먼저 읽지 않는다.** 눈으로 보는 셀러는 `n번 사진` 을 부르는데
            스크린리더만 파일명을 받으면 같은 장을 두 이름으로 부르게 된다(PRD `7.6.13b`). */}
        <span className="sr-only">
          {name} 못 만들었습니다. 판에 안 들어갑니다
          {retentionEnded ? `. ${RETENTION_ENDED_COPY.cardLine}` : ""}
        </span>
      </span>
    );
  }

  return (
    <button
      type="button"
      className={`cell${on ? "" : " off"}`}
      // ⛔ **`제외`·`빼기` 가 아니다.** 사진은 그대로 있고 이번 판에만 안 들어간다(`7.5.9`).
      aria-label={`${name} 판에서 ${on ? "끄기" : "켜기"}`}
      aria-pressed={!on}
      onClick={onToggle}
    >
      {inner}
    </button>
  );
}

